import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetch, apiPost, apiPut, fmtDate } from "@/lib/shieldx";
import { Loader, Panel } from "@/components/shieldx/Primitives";
import { StatusBadge, RiskBadge, DemoTag } from "@/components/shieldx/Badges";
import { ArrowLeft, Sparkles, ShieldCheck, Clock, FileText, Network, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATES = ["NEW", "INVESTIGATING", "CONTAINED", "BLOCKED", "ISOLATED", "RECOVERING", "RESOLVED", "FALSE_POSITIVE"];

export default function IncidentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: inc, loading, reload } = useFetch(`/incidents/${id}`, [id]);
  const [analyzing, setAnalyzing] = useState(false);
  const [note, setNote] = useState("");
  if (loading || !inc) return <Loader label="Loading incident" />;

  const runAnalysis = async () => {
    setAnalyzing(true);
    try { await apiPost(`/incidents/${inc.id}/analyze`, {}); toast.success("Incident analysis complete"); reload(); }
    catch { toast.error("Analysis unavailable"); } finally { setAnalyzing(false); }
  };
  const setState = async (state) => {
    try { await apiPut(`/incidents/${inc.id}`, { state }); toast.success(`Incident marked ${state}`); reload(); }
    catch { toast.error("Update failed"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await apiPut(`/incidents/${inc.id}`, { analyst_note: note }); setNote(""); toast.success("Analyst note recorded"); reload(); }
    catch { toast.error("Failed"); }
  };

  const a = inc.analysis;

  return (
    <div>
      <button onClick={() => navigate("/app/incidents")} data-testid="incident-back" className="mb-4 flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200">
        <ArrowLeft className="h-4 w-4" /> Incidents
      </button>

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm text-slate-400">{inc.incident_id}</span>
            <RiskBadge value={inc.risk_level} />
            <StatusBadge value={inc.state} />
            <DemoTag />
          </div>
          <h1 className="mt-2 font-display text-2xl font-bold text-slate-50 sm:text-3xl">{inc.title}</h1>
          <p className="mt-1 text-sm text-slate-400">{inc.category} · {inc.target_asset} · Opened {fmtDate(inc.created_at)}</p>
        </div>
        <button onClick={runAnalysis} disabled={analyzing} data-testid="run-analysis"
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
          {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Run Incident Analysis
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {/* Analysis */}
          <Panel title="Incident Analysis" desc="Independent assessment — recommends actions only; it cannot execute them">
            {!a ? (
              <p className="text-sm text-slate-500">No analysis yet. Run the analysis engine to generate a threat assessment and recommended response.</p>
            ) : (
              <div className="space-y-4">
                <p className="text-sm leading-relaxed text-slate-300">{a.summary}</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Mini label="Attack Vector" value={a.attack_vector} />
                  <Mini label="Attribution" value={a.attribution_confidence} />
                  <Mini label="Blast Radius" value={a.blast_radius} wide />
                  <Mini label="Priority" value={a.containment_priority} />
                </div>
                <div>
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Recommended Response</p>
                  <div className="space-y-2">
                    {(a.recommended_actions || []).map((r, i) => (
                      <div key={i} className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-200">{r.action}</p>
                          <p className="text-xs text-slate-500">{r.rationale}</p>
                        </div>
                        {r.requires_human_approval && (
                          <span className="shrink-0 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] text-amber-300">HUMAN APPROVAL</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <p className="font-mono text-[10px] text-slate-600">Engine: {a._engine}</p>
              </div>
            )}
          </Panel>

          {/* Timeline */}
          <Panel title="Response Timeline" desc="Detect → Validate → Warn → Block → Isolate → Recover">
            <div className="relative space-y-4 pl-6">
              <span className="absolute left-[7px] top-1 h-[calc(100%-1rem)] w-px bg-slate-800" />
              {(inc.timeline || []).map((t, i) => (
                <div key={i} className="relative">
                  <span className="absolute -left-[22px] top-1 h-3.5 w-3.5 rounded-full border-2 border-blue-500 bg-slate-950" />
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-slate-500" />
                    <p className="font-display text-sm font-semibold text-slate-200">{t.label}</p>
                    <span className="font-mono text-[11px] text-slate-500">{fmtDate(t.ts)}</span>
                  </div>
                  <p className="mt-0.5 text-sm text-slate-400">{t.detail}</p>
                </div>
              ))}
            </div>
          </Panel>

          {/* Evidence */}
          <Panel title="Evidence" desc="Technically observed, preserved with integrity hashes">
            <div className="space-y-2">
              {(inc.evidence || []).map((e, i) => (
                <div key={i} className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
                  <div className="flex-1">
                    <p className="text-sm text-slate-200">{e.detail}</p>
                    <p className="font-mono text-[11px] text-slate-500">{e.type} · sha256:{e.hash}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Automated Actions Taken</p>
              <div className="flex flex-wrap gap-1.5">
                {(inc.automated_actions || []).map((x) => <span key={x} className="rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-300">{x}</span>)}
              </div>
            </div>
          </Panel>

          {/* Analyst actions */}
          <Panel title="Analyst Actions" desc="Human-in-the-loop log">
            <div className="space-y-2">
              {(inc.analyst_actions || []).length === 0 && <p className="text-sm text-slate-500">No analyst actions yet.</p>}
              {(inc.analyst_actions || []).map((n, i) => (
                <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 text-sm">
                  <p className="text-slate-300">{n.note}</p>
                  <p className="mt-1 font-mono text-[11px] text-slate-500">{n.actor} · {fmtDate(n.ts)}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="analyst-note-input"
                placeholder="Add an analyst note…" className="h-10 flex-1 rounded-lg border border-slate-800 bg-slate-900/70 px-3 text-sm text-slate-100 outline-none focus:border-blue-500/50" />
              <button onClick={addNote} data-testid="analyst-note-add" className="rounded-lg bg-slate-800 px-4 text-sm font-medium text-slate-200 hover:bg-slate-700">Add</button>
            </div>
          </Panel>
        </div>

        {/* Side: source intel + state */}
        <div className="space-y-4">
          <Panel title="Source Intelligence" desc="Technically observed only — never fabricated">
            <dl className="space-y-2.5 text-sm">
              {[["Source IP", inc.source_ip],["ASN", inc.asn],["Network Provider", inc.network_provider],["Country (est.)", inc.country],["VPN / Proxy / Tor", inc.vpn_proxy_tor],["Account", inc.account],["Endpoint", inc.endpoint],["Auth Result", inc.auth_result],["Request Pattern", inc.request_pattern]].map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-3 border-b border-slate-800/60 pb-2">
                  <dt className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{k}</dt>
                  <dd className={`text-right font-mono text-xs ${["UNKNOWN","UNVERIFIED"].includes(String(v)) ? "text-amber-400" : "text-slate-200"}`}>{v || "UNKNOWN"}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p className="text-xs text-slate-400">Identity and exact location are never fabricated. Unverifiable attribution is shown as UNKNOWN / UNVERIFIED.</p>
            </div>
          </Panel>

          <Panel title="Threat Intelligence">
            <div className="flex flex-wrap gap-1.5">
              {(inc.ti_matches || ["None"]).map((t) => <span key={t} className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-xs text-slate-300">{t}</span>)}
            </div>
            <p className="mt-3 text-xs text-slate-500">Recovery status: <span className="text-slate-300">{inc.recovery_status}</span></p>
          </Panel>

          <Panel title="Workflow State" desc="Update incident state">
            <div className="grid grid-cols-2 gap-2">
              {STATES.map((s) => (
                <button key={s} onClick={() => setState(s)} data-testid={`state-${s}`}
                  className={`rounded-lg border px-2 py-2 font-mono text-[11px] ${inc.state === s ? "border-blue-500/50 bg-blue-600/15 text-blue-300" : "border-slate-800 bg-slate-900/50 text-slate-400 hover:text-slate-200"}`}>
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, wide }) {
  return (
    <div className={`rounded-lg border border-slate-800 bg-slate-900/60 p-3 ${wide ? "col-span-2" : ""}`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-1 text-sm text-slate-200">{value}</p>
    </div>
  );
}
